import { shift } from '@floating-ui/dom';
import { ReactRenderer } from '@tiptap/react';
import type { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import { MentionList } from './MentionList';
import { Mention } from '..';
import omit from 'lodash/omit';
import uniqBy from 'lodash/uniqBy';
import API_2_0 from 'Shared/api_2_0';
import * as style from './mentions.module.css';

export interface User {
   unique_username: string;
   username: string;
   rank?: number;
   image: string;
}

const MAX_USERS_SUGGESTED = 5;
// This should always be set to a lower than legitimate rank
const DEFAULT_RANK = 6;

function getUserMentionDetails({ query }: { query: string }) {
   const userlist = Object.values(App.users || {});

   // If no search query, show the list of users participating in the
   // disussions on the page.
   if (!query && userlist) {
      // Grab the user list from the page
      return userlist.slice(0, MAX_USERS_SUGGESTED);
   }

   /**
    * Rank users for sorting displayed suggestions.
    * User ranking map (1 is highest rank):
    * 1. unique_username exact match
    * 2. username exact match
    * 3. unique_username start of string match
    * 4. username start of string match
    * 5. username or unique_username substring match
    */

   const matchedLocalUsers = userlist
      // @ts-expect-error TS(2345) FIXME: Argument of type '(u: User) => { rank: number; uni... Remove this comment to see the full error message
      .map((u: User) => {
         let rank = DEFAULT_RANK; // Default rank to lowest value
         if (u.unique_username === query) {
            rank = 1;
         } else if (u.username === query) {
            rank = 2;
         } else if (u.unique_username.startsWith(query)) {
            rank = 3;
         } else if (u.username.startsWith(query)) {
            rank = 4;
         } else if (u.unique_username.includes(query) || u.username.includes(query)) {
            rank = 5;
         }

         return { ...u, rank: rank };
      })
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      .filter((u: User) => u.rank < DEFAULT_RANK)
      // Sort matched users based on how well they matches query (i.e. rank)
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      .sort((u1: User, u2: User) => u1.rank - u2.rank)
      // Drop the rank field after its use in sorting
      .map((u: User) => omit(u, ['rank']));

   // If there are more than MAX_USERS_SUGGESTED local users that match the
   // query, show that list and don't make a time-consuming request.
   if (matchedLocalUsers.length >= MAX_USERS_SUGGESTED) {
      return matchedLocalUsers.slice(0, MAX_USERS_SUGGESTED);
   }

   // If there is a search query this api request will fire. Note this
   // request only returns username, unique_username, and mini version
   // of their avatar image
   return new API_2_0(`users/search/${query}?limit=${MAX_USERS_SUGGESTED}&uniqueOnly`, {
      method: 'GET',
   })
      .send()
      .then((response: any) => {
         const items = response.results
            .filter((result: any) => result.username !== null && result.unique_username !== null)
            .map((result: any) => ({
               ...result,
               image: result.image['mini'],
            }));

         const noDuplicateUsers = uniqBy(matchedLocalUsers.concat(items), 'unique_username');
         return noDuplicateUsers.slice(0, MAX_USERS_SUGGESTED);
      });
}

function renderMentions() {
   let reactRenderer: any;
   let unmount: (() => void) | undefined;

   return {
      onStart: (props: SuggestionProps) => {
         reactRenderer = new ReactRenderer(MentionList, {
            props,
            editor: props.editor,
            className: 'z-tooltip max-w-xs',
         });
         unmount = props.mount(reactRenderer.element);
      },
      onUpdate(props: SuggestionProps) {
         reactRenderer?.updateProps(props);
      },
      // No Escape case: the suggestion plugin exits on Escape itself.
      onKeyDown(props: SuggestionKeyDownProps) {
         return reactRenderer?.ref?.onKeyDown(props) ?? false;
      },
      onExit() {
         unmount?.();
         reactRenderer?.destroy();
         unmount = undefined;
         reactRenderer = undefined;
      },
   };
}

export const CustomMention = Mention.configure({
   HTMLAttributes: {
      class: style.mention,
   },
   suggestion: {
      items: getUserMentionDetails,
      render: renderMentions,
      // tippy.js's defaults, which the popup had before props.mount(): a
      // 10px gap, and kept 5px inside the viewport instead of overflowing it.
      offset: { mainAxis: 10 },
      floatingUi: { middleware: [shift({ padding: 5 })] },
      // Modified from @tiptap/extension-mention
      command: ({ editor, range, props }) => {
         // increase range.to by one when the next node is of type "text"
         // and starts with a space character
         const nodeAfter = editor.view.state.selection.$to.nodeAfter;
         const overrideSpace = nodeAfter?.text?.startsWith(' ');

         if (overrideSpace) {
            range.to += 1;
         }

         editor
            .chain()
            .focus()
            .insertContentAt(range, [
               {
                  type: 'mention',
                  attrs: props,
               },
               {
                  type: 'text',
                  text: ' ',
               },
            ])
            .run();

         // Needed to fix the browser's selection after clicking on a suggestion
         // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
         window.getSelection().collapseToEnd();
      },
   },
});
